import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TransformationSection = () => {
  const benefits = [
    {
      icon: "Zap",
      text: "Recuperar tu energía"
    },
    {
      icon: "Target",
      text: "Aprender hábitos sostenibles adaptados a tu edad y estilo de vida"
    },
    {
      icon: "Heart",
      text: "Transformar tu relación con la comida para siempre y disfrutar comiendo sano y entrenando de forma inteligente"
    },
    {
      icon: "Users",
      text: "Recibir acompañamiento personalizado en cada paso de tu transformación"
    }
  ];

  const forYouPoints = [
    "Estás lista para dejar de sufrir",
    "Quieres un cambio real, no un parche temporal",
    "Estás dispuesta a dejar de buscar excusas"
  ];

  const notForYouPoints = [
    "Crees en milagros",
    "Buscas una pastilla mágica",
    "No estás dispuesta a comprometerte con tu bienestar"
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/10 to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Header */}
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground mb-6 leading-tight">
            Este no es otro programa de hábitos.
          </h2>
          <h3 className="font-heading text-2xl md:text-3xl text-primary mb-8">
            Es el momento de que vuelvas a ti.
          </h3>

          <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
            <p>
              Si estás aquí es porque en el fondo sabes que mereces más.<br />
              Más energía. Más calma. Más libertad.<br />
              Y menos culpa, menos dietas, menos exigencias imposibles.
            </p>

            <p className="font-body-semibold text-foreground">
              No tienes que castigarte para sentirte bien en tu piel.<br />
              Tampoco tienes que luchar sola.
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <picture>
                <source
                  srcSet="/assets/images/optimized/miri_cooking-sm.webp 480w,
                          /assets/images/optimized/miri_cooking-md.webp 768w,
                          /assets/images/optimized/miri_cooking-lg.webp 1200w,
                          /assets/images/optimized/miri_cooking-xl.webp 1600w,
                          /assets/images/optimized/miri_cooking.webp 1800w"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw"
                  type="image/webp"
                />
                <source
                  srcSet="/assets/images/optimized/miri_cooking-sm.jpg 480w,
                          /assets/images/optimized/miri_cooking-md.jpg 768w,
                          /assets/images/optimized/miri_cooking-lg.jpg 1200w,
                          /assets/images/optimized/miri_cooking-xl.jpg 1600w,
                          /assets/images/optimized/miri_cooking.jpg 1800w"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw"
                  type="image/jpeg"
                />
                <img
                  src="/assets/images/optimized/miri_cooking.webp"
                  alt="Miri Espejo preparando comida saludable - nutrición antiinflamatoria real"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                  loading="lazy"
                />
              </picture>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8 order-1 lg:order-2">
            <div className="space-y-4">
              <p className="text-lg text-muted-foreground leading-relaxed">
                Con mi acompañamiento aprenderás a <span className="font-body-semibold text-foreground">escuchar a tu cuerpo</span> en lugar de pelear con él.
                A <span className="font-body-semibold text-foreground">comer con placer y sin miedo</span>.
                A construir hábitos que se quedan contigo porque encajan en tu vida real, no en un plan perfecto que nadie sostiene.
              </p>

              <p className="text-lg text-muted-foreground leading-relaxed">
                Y lo harás acompañada, con otras mujeres que como tú, un día dijeron:
                <span className="font-body-semibold text-primary block mt-2">
                  "Basta de odiar mi cuerpo. Es hora de cuidarlo."
                </span>
              </p>

              <div className="bg-card border-l-4 border-primary p-6 rounded-lg shadow-card-shadow">
                <p className="font-heading text-xl text-foreground mb-2">
                  Esto no va de dietas.
                </p>
                <p className="font-body text-muted-foreground">
                  Va de ti. De recuperar tu energía, tu fuerza y tu manera única de brillar.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Problem Statement */}
        <div className="bg-muted/30 rounded-2xl p-8 md:p-12 mb-16">
          <h3 className="font-heading text-2xl md:text-3xl text-foreground mb-6">
            ¿Cansada de vivir a medias?
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            Llevas años luchando con la báscula. Contando cada caloría. Sintiéndote culpable por un simple trozo de pan.
            Pero lo peor no es el peso, es la fatiga. Es vivir con el piloto automático puesto, sin la energía para disfrutar de tu gente o de lo que te gusta.
          </p>
          <p className="font-heading text-xl text-primary">
            Transformación, no restricción
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed mt-4">
            Deja de pelearte con la comida. Olvídate de los gimnasios que te prometen cuerpos de revista en un mes.
            Miri Espejo no te va a vender una dieta, te va a enseñar a vivir. A recuperar la energía que perdiste en el camino
            y a redescubrir el placer de comer sin miedo.
          </p>
        </div>

        {/* For You / Not For You */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* For You */}
          <div className="bg-card border-2 border-success rounded-2xl p-8 shadow-card-shadow">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-success" />
              </div>
              <h4 className="font-heading text-xl text-foreground">
                Esto es para ti si:
              </h4>
            </div>
            <ul className="space-y-3">
              {forYouPoints.map((point, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Not For You */}
          <div className="bg-card border-2 border-destructive/30 rounded-2xl p-8 shadow-card-shadow">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center">
                <Icon name="XCircle" size={24} className="text-destructive" />
              </div>
              <h4 className="font-heading text-xl text-foreground">
                Esto NO es para ti si:
              </h4>
            </div>
            <ul className="space-y-3">
              {notForYouPoints.map((point, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <Icon name="X" size={20} className="text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Community */}
        <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 md:p-12 mb-16 text-center">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="Users" size={32} className="text-primary-foreground" />
          </div>
          <h4 className="font-heading text-2xl text-foreground mb-4">
            Y no lo harás sola
          </h4>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Conoce a otras mujeres que están en la misma batalla que tú.
            Mujeres que, como tú, están hartas de promesas vacías y están listas para un cambio de verdad.
          </p>
        </div>

        {/* Benefits Grid */}
        <div>
          <h3 className="font-heading text-2xl md:text-3xl text-foreground mb-8 text-center">
            Lo que conseguirás
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 bg-card rounded-xl p-6 shadow-card-shadow hover:shadow-card-elevated transition-smooth"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon
                    name={benefit.icon}
                    size={20}
                    className="text-primary"
                  />
                </div>
                <p className="text-foreground text-base leading-relaxed font-body">
                  {benefit.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformationSection;
